'use client'
import clsx from 'clsx';
import * as Clerk from '@clerk/elements/common'
import * as SignIn from '@clerk/elements/sign-in'
import { useSignIn } from '@clerk/nextjs'
import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react";
import { useIntl, FormattedMessage } from 'react-intl';
import { KeenIcon } from '../common/keenicons';
import { ScreenLoader } from '../common';


export default function SignInForm() {
    const [showPassword, setShowPassword] = useState(false);
    const { formatMessage } = useIntl();
    const { isLoaded } = useSignIn()

    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [formValid, setFormValid] = useState(false);

    useEffect(() => {
        const noErrors =
            !emailError &&
            !passwordError
        const allFilled =
            email &&
            password
        setFormValid(Boolean(noErrors && allFilled));
    }, [
        emailError,
        passwordError,
        email,
        password,
    ]);

function validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

    if (!isLoaded) {
        return <ScreenLoader />
    }

    return (
        <div className="card max-w-[390px] w-full">
            <SignIn.Root>
                <Clerk.Loading>
                    {(isGlobalLoading) => (
                        <>
                            <SignIn.Step
                                name="start"
                                className="card-body flex flex-col gap-5 p-10" id="sign_in_form"
                            >
                                <div className="text-center mb-2.5">
                                    <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5"><FormattedMessage id="SIGN_IN.SIGN_IN_TEXT" /></h3>
                                    <div className="flex items-center justify-center font-medium">
                                        <span className="text-2sm text-gray-600 me-1.5">
                                            <FormattedMessage id="SIGN_IN.NEED_AN_ACCOUNT" />
                                        </span>
                                        <Link
                                            href="/sign-up"
                                            className="text-2sm link"
                                        >    <FormattedMessage id="SIGN_IN.SIGN_UP_TEXT" /></Link>
                                    </div>
                                </div>

                                <Clerk.GlobalError
                                    role="alert"
                                    className="flex items-center gap-2.5 border rounded-md p-3 border-danger-clarity bg-danger-light text-danger"
                                >
                                    {(error) => (
                                        <div className="flex items-center gap-2.5">
                                            <KeenIcon icon="information-2" className="text-lg leading-none text-danger" />
                                            <div className="text-gray-700 text-sm">{error.message}</div>
                                        </div>
                                    )}
                                </Clerk.GlobalError>


                                <div className="grid grid-cols-2 gap-2.5">
                                    <Clerk.Connection
                                        name="google"
                                        className="btn btn-light btn-sm justify-center"
                                    >
                                        <Image
                                            src="/assets/media/brand-logos/google.svg"
                                            alt="Google"
                                            width={16}
                                            height={16}
                                            className="size-3.5 shrink-0"
                                        />
                                        <FormattedMessage id="USE_GOOGLE" />
                                    </Clerk.Connection>
                                    <Clerk.Connection
                                        name="apple"
                                        className="btn btn-light btn-sm justify-center"
                                    >
                                        <Image src="/assets/media/brand-logos/apple-black.svg" className="size-3.5 shrink-0 dark:hidden" alt="Apple" width={16}
                                            height={16} />
                                        <Image src="/assets/media/brand-logos/apple-white.svg" className="size-3.5 shrink-0 light:hidden" alt="Apple" width={16}
                                            height={16} />
                                        <FormattedMessage id="USE_APPLE" />
                                    </Clerk.Connection>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="border-t border-gray-200 w-full" />
                                    <span className="text-2xs text-gray-500 font-medium uppercase"><FormattedMessage id="OR" /></span>
                                    <span className="border-t border-gray-200 w-full" />
                                </div>
                                <Clerk.Field name="identifier" className="flex flex-col gap-1">
                                    <Clerk.Label className="form-label text-gray-900"><FormattedMessage id="EMAIL" /></Clerk.Label>
                                    <label className="input">
                                        <Clerk.Input
                                            type="email"
                                            className="form-control"
                                            placeholder={formatMessage({ id: 'EMAIL_PLACEHOLDER' })}
                                            value={email}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setEmail(value);
                                                if (value && validateEmail(value)) {
                                                    setEmailError('');
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const value = e.target.value;
                                                if (!value) {
                                                    setEmailError(formatMessage({ id: 'VALIDATION.EMAIL_REQUIRED' }));
                                                } else if (!validateEmail(value)) {
                                                    setEmailError(formatMessage({ id: 'VALIDATION.EMAIL_INVALID' }));
                                                } else {
                                                    setEmailError('');
                                                }
                                            }}
                                        />
                                    </label>
                                    {emailError && <span className="text-danger text-xs mt-1">{emailError}</span>}
                                    <Clerk.FieldError className="text-danger text-xs mt-1" />
                                </Clerk.Field>
                                <div className="flex flex-col gap-1">
                                    <Clerk.Field name="password" >
                                        <div className="flex items-center justify-between gap-1 mb-1">
                                            <Clerk.Label className="form-label text-gray-900"><FormattedMessage id="PASSWORD" /></Clerk.Label>
                                            <Link
                                                href="/forget-password"
                                                className="text-2sm link shrink-0"
                                            >
                                                <FormattedMessage id="SIGN_IN.FORGOT_PASSWORD" />
                                            </Link>
                                        </div>
                                        <label htmlFor="" className="input">
                                            <Clerk.Input
                                                type={showPassword ? 'text' : 'password'}
                                                className="form-control"
                                                value={password}
                                                placeholder={formatMessage({ id: 'PASSWORD_PLACEHOLDER' })}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setPassword(value);
                                                    if (value) setPasswordError('');
                                                }}
                                                onBlur={(e) => {
                                                    const value = e.target.value;
                                                    setPasswordError(value ? '' : formatMessage({ id: 'VALIDATION.PASSWORD_REQUIRED' }));
                                                }}
                                            />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="btn btn-icon" >
                                                <KeenIcon icon="eye" className={clsx('text-gray-500', { hidden: showPassword })} />
                                                <KeenIcon
                                                    icon="eye-slash"
                                                    className={clsx('text-gray-500', { hidden: !showPassword })}
                                                />
                                            </button>
                                        </label>
                                        {passwordError && <span className="text-danger text-xs mt-1">{passwordError}</span>}
                                        <Clerk.FieldError className="text-danger text-xs mt-1" />
                                    </Clerk.Field>
                                </div>
                                {/* <label className="checkbox-group">
                                    <input className="checkbox checkbox-sm" type="checkbox" />
                                    <span className="checkbox-label"><FormattedMessage id="SIGN_IN.REMEMBER_ME" /></span>
                                </label> */}
                                <SignIn.Action submit asChild>
                                    <button disabled={!formValid || isGlobalLoading} className="btn btn-primary flex justify-center grow">
                                        <Clerk.Loading>
                                            {(isLoading) => {
                                                return isLoading ? (
                                                      <FormattedMessage id="LOADING" />  
                                                ) : (
                                                    <FormattedMessage id="SIGN_IN.SIGN_IN_TEXT" />
                                                )
                                            }}
                                        </Clerk.Loading>
                                    </button>
                                </SignIn.Action>
                            </SignIn.Step>
                        </>
                    )}
                </Clerk.Loading>
            </SignIn.Root>
        </div >
    )
}